import AppContext from 'App/AppContext';
import TasksView, { TaskViewType } from 'components/Tasks/View/TasksView';
import { globalStackTokensGapMedium, globalTextStylesBold } from 'globalStyles';
import ProcessingActivity from 'models/processingActivity';
import Task from 'models/tasks/task';
import Tasks from 'models/tasks/tasks';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetTasksForProcessingActivity,
  apiGetTasksForProcessingActivityViewSchedule,
} from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { LocalStorageKeys } from 'utils/localstorage';

import { IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import { getViewFromURLParams } from 'components/Filter/PeriodFilterHelper';
import { QuickFilterContext } from 'components/Filter/QuickFilter';
import { getFilterFromURLParams } from 'components/Tasks/Filter/FilterTasksHelper';
import { getEntity } from 'globalFunctions';
import { getRandomId } from 'utils/string';
import { removeUrlQuery } from 'utils/url';

interface ITabTasksProps {
  selectedProcessingActivity?: ProcessingActivity;
  isLoading: boolean;
}

const TabTasks = (props: ITabTasksProps) => {
  const { t } = useTranslation(['translation', 'tasks', 'processingActivity']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [reloadKey, setReloadKey] = useState<string>(getRandomId());
  const [filterFromUrl, setFilterFromUrl] = useState<string[] | undefined>(undefined);
  const [viewTypeFromUrl, setViewTypeFromUrl] = useState<TaskViewType | undefined>(undefined);

  useEffect(() => {
    setFilterFromUrl(getFilterFromURLParams(appContext));
    setViewTypeFromUrl(getViewFromURLParams());
    removeUrlQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.selectedProcessingActivity) {
      setReloadKey(getRandomId());
    }
  }, [props.selectedProcessingActivity]);

  const loadFilteredData = async (
    view: TaskViewType,
    filter: string[],
    memberFilter: string[],
    start: Date | undefined,
    end: Date | undefined,
  ) => {
    try {
      if (isLoading || !props.selectedProcessingActivity || !props.selectedProcessingActivity.processingActivityId) {
        return;
      }

      setIsLoading(true);
      appContext.showContentLoader();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let tasksResponse: Tasks;

      switch (view) {
        case TaskViewType.Timeline:
          tasksResponse = await apiGetTasksForProcessingActivity(
            props.selectedProcessingActivity.processingActivityId,
            undefined,
            undefined,
            accessToken,
            appContext.globalDataCache,
          );
          break;
        case TaskViewType.List:
        case TaskViewType.Board:
          tasksResponse = await apiGetTasksForProcessingActivity(
            props.selectedProcessingActivity.processingActivityId,
            start,
            end,
            accessToken,
            appContext.globalDataCache,
          );
          break;
        case TaskViewType.Schedule:
          if (!start || !end) return;
          tasksResponse = await apiGetTasksForProcessingActivityViewSchedule(
            props.selectedProcessingActivity.processingActivityId,
            start,
            end,
            accessToken,
            appContext.globalDataCache,
          );
          break;
      }

      setTasks(tasksResponse?.tasks ?? []);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsLoading(false);
    }
  };

  //
  // Commands
  //
  const getNewTask = (newTask: Task): Task => {
    if (props.selectedProcessingActivity) {
      newTask.relatedEntities = [getEntity(props.selectedProcessingActivity)];
    }

    return newTask;
  };

  const getNewTaskList = (template: Task | undefined, newTask: Task): Task => {
    if (props.selectedProcessingActivity) {
      newTask.relatedEntities = [getEntity(props.selectedProcessingActivity)];
    }

    return newTask;
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill tokens={globalStackTokensGapMedium}>
      <IPStack.Item>
        <IPText
          block
          variant="large"
          styles={globalTextStylesBold}
        >{`${props.selectedProcessingActivity?.code} ${props.selectedProcessingActivity?.name}`}</IPText>
        <IPText>{t('processingActivity:TabTasks.SubTitle')}</IPText>
      </IPStack.Item>
      <IPStack.Item grow>
        <TasksView
          tasks={tasks}
          isLoading={isLoading || props.isLoading}
          isUpdating={false}
          addExistingTaskContextEntity={
            props.selectedProcessingActivity ? getEntity(props.selectedProcessingActivity) : undefined
          }
          boardProps={{
            allowColumnReorder: false,
            taskStates: appContext.globalDataCache.taskStates.items,
            onGetNewTaskBoard: getNewTask,
          }}
          listProps={{
            selectionMode: IPSelectionMode.multiple,
            cacheKeySort: LocalStorageKeys.ProcessingActivityTaskListSort,
            onGetNewTaskList: getNewTaskList,
          }}
          scheduleProps={{
            hideContext: true,
          }}
          timelineProps={{
            onGetNewTaskTimeline: getNewTask,
          }}
          onLoad={loadFilteredData}
          onUpdateTasks={setTasks}
          reloadKey={reloadKey}
          filterStorageKey={LocalStorageKeys.FilterTasksProcessingActivity}
          quickFilterContext={QuickFilterContext.taskContext}
          initialFilter={filterFromUrl}
          initialViewType={viewTypeFromUrl}
        />
      </IPStack.Item>
    </IPStack>
  );
};

export default TabTasks;
